import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../state/auth-context';
import { ThemeToggle } from '../components/ThemeToggle';
import { NotificationBell } from '../components/NotificationBell';
import { NotificationProvider } from '../context/NotificationContext';
import { Button } from '../components/ui/button';
import { getTokens } from '../services/auth-client';
import { decodeJwtPayload, isAdmin, setDynamicAdminGroups } from '../services/jwt-utils';
import { appConfig } from '../config';
import { WhatsNewModal, useWhatsNew } from '../components/WhatsNewModal';
import { BreadcrumbProvider, useBreadcrumb } from '../state/breadcrumb-context';
import { useVersionCheck } from '../hooks/useVersionCheck';

const RETURN_URL_KEY = 'coreflow_return_url';

export function saveReturnUrl(url: string): void {
  sessionStorage.setItem(RETURN_URL_KEY, url);
}

export function consumeReturnUrl(): string | null {
  const url = sessionStorage.getItem(RETURN_URL_KEY);
  if (url) {
    sessionStorage.removeItem(RETURN_URL_KEY);
  }
  return url;
}

/**
 * Gets current user display info from the ID token.
 */
function getUserInfo(): { name: string; email: string } {
  const tokens = getTokens();
  if (!tokens) return { name: '', email: '' };
  const claims = decodeJwtPayload(tokens.idToken);
  return {
    name: claims?.name ?? claims?.email ?? '',
    email: claims?.email ?? '',
  };
}

/**
 * Breadcrumb derived from current path and reactive breadcrumb context.
 */
function Breadcrumbs(): React.ReactElement {
  const location = useLocation();
  const { state: breadcrumbState } = useBreadcrumb();
  const segments = location.pathname.split('/').filter(Boolean);

  const crumbs: Array<{ label: string; path: string }> = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const path = '/' + segments.slice(0, i + 1).join('/');

    // Map known segments to labels
    if (seg === 'boards' && i === 0) crumbs.push({ label: 'Boards', path });
    else if (seg === 'pages' && i === 0) crumbs.push({ label: 'Pages', path });
    else if (seg === 'analytics' && i === 0) crumbs.push({ label: 'Analytics', path });
    else if (seg === 'admin') crumbs.push({ label: 'Admin', path });
    else if (seg === 'notifications') crumbs.push({ label: 'Notifications', path });
    else if (seg === 'profile') crumbs.push({ label: 'Profile', path });
    else if (i > 0 && segments[i - 1] === 'boards') {
      crumbs.push({ label: breadcrumbState.boardTitle || 'Board', path });
    }
    else if (i > 0 && segments[i - 1] === 'pages') {
      crumbs.push({ label: breadcrumbState.libraryTitle || 'Library', path });
    }
    else if (i > 1 && segments[i - 2] === 'pages') {
      crumbs.push({ label: breadcrumbState.pageTitle || 'Page', path });
    }
    else if (seg === 'cards') continue; // skip 'cards' segment
    else if (seg === 'calendar') crumbs.push({ label: 'Calendar', path });
    else crumbs.push({ label: seg, path });
  }

  if (crumbs.length === 0) return <></>;

  return (
    <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1 text-sm text-muted-foreground">
      {crumbs.map((crumb, i) => (
        <React.Fragment key={crumb.path}>
          {i > 0 && <span className="mx-1">/</span>}
          {i === crumbs.length - 1 ? (
            <span className="text-foreground font-medium">{crumb.label}</span>
          ) : (
            <Link to={crumb.path} className="hover:text-foreground transition-colors">
              {crumb.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/**
 * Application header with nav, user info, and actions.
 */
function AppHeader(): React.ReactElement {
  const { logout } = useAuth();
  const location = useLocation();
  const user = getUserInfo();
  const [admin, setAdmin] = React.useState(isAdmin());
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

  // Re-check admin status after dynamic admin groups load
  React.useEffect(() => {
    // Check immediately and also poll briefly for the async admin group fetch to complete
    const checkAdmin = () => setAdmin(isAdmin());
    checkAdmin();
    const timer = setInterval(checkAdmin, 500);
    const timeout = setTimeout(() => clearInterval(timer), 3000);
    return () => { clearInterval(timer); clearTimeout(timeout); };
  }, []);

  // Fetch avatar from profile API
  React.useEffect(() => {
    let cancelled = false;
    async function fetchAvatar(): Promise<void> {
      try {
        const profile = await import('../services/api-client').then((api) =>
          api.get<{ avatarUrl?: string | null }>('/v1/users/me'),
        );
        if (!cancelled && profile.avatarUrl) {
          setAvatarUrl(profile.avatarUrl);
        }
      } catch {
        // Non-critical — keep initials fallback
      }
    }
    fetchAvatar();
    return () => { cancelled = true; };
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" role="banner">
      <div className="flex h-14 items-center px-4 md:px-6">
        {/* Left: App name + breadcrumbs */}
        <div className="flex items-center gap-4">
          <Link to="/boards" className="text-lg font-semibold tracking-tight hover:opacity-80 transition-opacity">
            CoreFlow
          </Link>
          <Breadcrumbs />
        </div>

        {/* Right: Nav links + user + actions */}
        <div className="ml-auto flex items-center gap-2">
          {/* Nav links */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            <Link to="/boards">
              <Button variant="ghost" size="sm">Boards</Button>
            </Link>
            <Link to="/pages">
              <Button variant="ghost" size="sm">Pages</Button>
            </Link>
            <Link to="/analytics">
              <Button variant="ghost" size="sm" className={location.pathname.startsWith('/analytics') ? 'bg-accent' : ''}>Analytics</Button>
            </Link>
            <WhatsNewButton />
            {admin && (
              <Link to="/admin">
                <Button variant="ghost" size="sm">Admin</Button>
              </Link>
            )}
          </nav>

          {/* Separator */}
          <div className="hidden md:block h-6 w-px bg-border mx-2" />

          {/* User info */}
          <div className="hidden sm:flex items-center gap-2 text-sm">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="h-7 w-7 rounded-full object-cover"
                width="28"
                height="28"
              />
            ) : (
              <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-muted-foreground max-w-[150px] truncate">
              {user.name || user.email || 'User'}
            </span>
          </div>

          {/* Theme toggle */}
          <ThemeToggle />

          {/* Notification bell */}
          <NotificationBell />

          {/* Settings link */}
          <Link to="/profile" aria-label="Profile settings">
            <Button variant="ghost" size="icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </Button>
          </Link>

          {/* Sign out */}
          <Button variant="ghost" size="sm" onClick={() => logout()} className="text-muted-foreground">
            Sign out
          </Button>
        </div>
      </div>
    </header>
  );
}

/**
 * Auth guard layout wrapper.
 */
export function AuthenticatedLayout(): React.ReactElement | null {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  // Fetch admin group ID from system settings on mount
  useEffect(() => {
    async function loadAdminGroups(): Promise<void> {
      try {
        const apiUrl = appConfig.apiUrl.replace(/\/$/, '');
        const response = await fetch(`${apiUrl}/v1/admin/settings`);
        if (response.ok) {
          const json = await response.json();
          const adminGroupId = json?.data?.adminGroupId;
          if (adminGroupId && typeof adminGroupId === 'string') {
            setDynamicAdminGroups([adminGroupId]);
          }
        }
      } catch {
        // Non-critical — admin role detection will fall back to static groups
      }
    }
    loadAdminGroups();
  }, []);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const currentPath = location.pathname + location.search + location.hash;
      saveReturnUrl(currentPath);
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, location, navigate]);

  if (isLoading) return null;
  if (!isAuthenticated) return null;

  return (
    <BreadcrumbProvider>
      <NotificationProvider>
        <WhatsNewProvider>
          <div className="h-screen flex flex-col bg-background overflow-hidden">
            <AppHeader />
            <VersionBanner />
            <div className="flex-1 min-h-0 overflow-auto">
              <Outlet />
            </div>
            <WhatsNewModalWrapper />
          </div>
        </WhatsNewProvider>
      </NotificationProvider>
    </BreadcrumbProvider>
  );
}

// ---------------------------------------------------------------------------
// Version Update Banner
// ---------------------------------------------------------------------------

function VersionBanner(): React.ReactElement | null {
  const { updateAvailable, dismiss } = useVersionCheck();

  if (!updateAvailable) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 bg-blue-600 px-4 py-2 text-sm text-white"
      role="status"
      aria-live="polite"
    >
      <span>A new version is available.</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded bg-white px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="rounded px-2 py-1 text-xs text-blue-200 hover:text-white"
          aria-label="Dismiss update notification"
        >
          Later
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What's New shared context
// ---------------------------------------------------------------------------

const WhatsNewContext = React.createContext<{
  isOpen: boolean;
  open: () => void;
  close: () => void;
  hasUnseen: boolean;
  currentVersion: string;
} | null>(null);

function WhatsNewProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const whatsNew = useWhatsNew();
  return <WhatsNewContext.Provider value={whatsNew}>{children}</WhatsNewContext.Provider>;
}

function useWhatsNewContext() {
  const ctx = React.useContext(WhatsNewContext);
  if (!ctx) throw new Error('useWhatsNewContext must be within WhatsNewProvider');
  return ctx;
}

function WhatsNewModalWrapper(): React.ReactElement {
  const { isOpen, close } = useWhatsNewContext();
  return <WhatsNewModal isOpen={isOpen} onClose={close} />;
}

function WhatsNewButton(): React.ReactElement {
  const { open, hasUnseen } = useWhatsNewContext();
  return (
    <Button variant="ghost" size="sm" onClick={open} className="relative">
      What's New
      {hasUnseen && (
        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" aria-label="New updates available" />
      )}
    </Button>
  );
}
